/** Shared types and constants of the guide stage (stage.tsx): kept apart so the stage itself stays within reach of working memory. */
import { createContext, useContext } from 'react';
import type { CharView, Who } from './Character';
import type { Eyes, PresenterState } from './CharacterPresenter';
import type { P } from './motion';
import type { BeatId } from './beats';
import type { Destination, Step } from '@/data/session';

export type Target = { host: HTMLElement; x: number; y: number };
export type Dir = 'down' | 'up';
export type LedgerStepInfo = {
  index: number;
  total: number;
  step: Step;
  paneHost: HTMLElement;
  viewEl: HTMLElement;
  slots: { clippy: HTMLElement; foxy: HTMLElement } | null;
  mailFromEl: HTMLElement | null;
  containerEl: HTMLElement;
  sectionEl: HTMLElement;
  /** step 1's landing spot (fractions of the view): B1 always lands there, then he hops to the current step */
  landing: { x: number; y: number };
};
export type Reaction = { state?: PresenterState; eyes?: Eyes; lookAt?: Element | P | null; nod?: boolean; leanBack?: boolean; pop?: boolean; blink?: boolean; /** the reader's own action: reaches him when its section is in view, owner or not */ user?: boolean };

export type StageApi = {
  ledgerOwns: (owns: boolean, info: LedgerStepInfo, dir: Dir) => void;
  ledgerStep: (info: LedgerStepInfo) => void;
  toss: (text: string, dir: Destination, toEl: HTMLElement) => void;
  react: (section: string, who: Who, r: Reaction) => void;
  owner: () => string;
};

export const StageContext = createContext<StageApi | null>(null);
export const useStage = () => useContext(StageContext);

// Mr. Clippy's sizes are his rendered HEIGHT (the wire spans 0.88 of its box); Foxy's box is tight.
export const SIZES = { clippy: { pair: 100, frame: 91, dock: 73 }, foxy: { pair: 94, frame: 85 } } as const; // Foxy's ink is 0.94 of her box: 88px tall in the pair, 80px on frames
export const GUTTER = 72;
// the float at rest in the gutter
export const REST_VIEW: Partial<CharView> = { state: 'listening', act: 'float', eyes: { mode: 'neutral', key: 'rest' }, facing: 'left', say: null, action: 'idle', tilt: 0, lean: 0, persona: { skeptic: 0, lean: 0, breath: 3.2 }, emote: null, motion: [] };
// the sections that own eye beats once he floats (most visible ≥ 40% wins)
export const SECTIONS: { sel: string; beat: BeatId }[] = [
  { sel: '.fx-funnel', beat: 'B14' }, { sel: '.fx-ob', beat: 'B15' }, { sel: '.fx-infra', beat: 'B16' }, { sel: '.fx-how', beat: 'B17' },
  { sel: '.fx-two', beat: 'B18' }, { sel: '.fx-ent', beat: 'B19' }, { sel: '.fx-faq', beat: 'B20' }, { sel: '.fx-invite', beat: 'B21' },
];
export const base = (who: Who): CharView => ({
  state: 'listening', eyes: { mode: 'neutral' }, persona: { breath: 3 }, facing: who === 'clippy' ? 'right' : 'left', size: SIZES[who].frame,
  emote: null, motion: [], say: null, badge: false, dust: 0, action: 'idle', act: 'session', tilt: 0, lean: 0, hidden: true, docked: false,
});
export const eyeLine = (who: Who, size: number) => (who === 'clippy' ? -0.4 * 0.55 * size : 0.05 * 0.94 * size); // Foxy's eyes sit at 0.55 of her ink: 0.05 H below the box centre
export const unit = (dx: number, dy: number): P => {
  const n = Math.hypot(dx, dy) || 1;
  return { x: dx / n, y: dy / n };
};

